import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Image as ImageIcon, LayoutList, QrCode, Trash2, Type } from 'lucide-react';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import DynamicBadgeRenderer from './DynamicBadgeRenderer';
import { TEXT_CASES } from '../utils/badgeText';
import { buildStackedFields, newRow, rowsFromFields, spacingFromFields } from '../utils/badgeQuickLayout';

const TYPE_META = {
  text: { label: 'Text', icon: Type },
  qrCode: { label: 'QR code', icon: QrCode },
  image: { label: 'Image', icon: ImageIcon },
};

const selectClass = 'w-full h-9 rounded-md border border-gray-300 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

const NumberField = ({ label, value, onChange, step = 0.05, min = 0, max }) => (
  <div>
    <Label className="text-xs text-gray-600">{label}</Label>
    <Input
      type="number"
      step={step}
      min={min}
      max={max}
      value={value}
      onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
      className="h-9"
    />
  </div>
);

/**
 * Form-based alternative to drag and drop: pick fields in order, set paper size and spacing,
 * and get the same field config the designer saves.
 */
const BadgeQuickLayoutDialog = ({
  open,
  onClose,
  paperSizes,
  paperKey: initialPaperKey,
  customSize: initialCustomSize,
  fields,
  backgroundImage,
  fieldSuggestions = [],
  dummyQR,
  onApply,
}) => {
  const [paperKey, setPaperKey] = useState(initialPaperKey);
  const [customSize, setCustomSize] = useState(initialCustomSize);
  const [spacing, setSpacing] = useState({ top: 1, gap: 0.1, sidePadding: 0.2 });
  const [rows, setRows] = useState([]);
  const [showBackground, setShowBackground] = useState(true);

  const paper = paperKey === 'custom' ? customSize : paperSizes[paperKey];

  // Start from the current design each time the helper opens.
  useEffect(() => {
    if (!open) return;
    const startPaper = initialPaperKey === 'custom' ? initialCustomSize : paperSizes[initialPaperKey];
    setPaperKey(initialPaperKey);
    setCustomSize(initialCustomSize);
    setSpacing(spacingFromFields(fields, startPaper));
    setRows(fields?.length
      ? rowsFromFields(fields)
      : [
        newRow('text', { key: 'name', label: 'Name', fontSize: 18, bold: true }),
        newRow('qrCode'),
        newRow('text', { key: 'userId', label: 'User ID', fontSize: 11, textCase: 'none' }),
      ]);
  }, [open]);

  const generated = useMemo(() => buildStackedFields({
    paper,
    top: Number(spacing.top) || 0,
    gap: Number(spacing.gap) || 0,
    sidePadding: Number(spacing.sidePadding) || 0,
    rows,
  }), [paper, spacing, rows]);

  const bottom = generated.length ? generated[generated.length - 1].y + generated[generated.length - 1].height : 0;
  const overflows = bottom > paper.height;

  const previewData = useMemo(() => {
    const data = { qrCodeBase64: dummyQR };
    rows.forEach((row) => {
      if (row.type === 'text' && row.key) data[row.key] = row.label || row.key;
    });
    return data;
  }, [rows, dummyQR]);

  const updateRow = (index, patch) => setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const moveRow = (index, delta) => setRows((prev) => {
    const next = [...prev];
    const target = index + delta;
    if (target < 0 || target >= next.length) return prev;
    [next[index], next[target]] = [next[target], next[index]];
    return next;
  });
  const removeRow = (index) => setRows((prev) => prev.filter((_, i) => i !== index));
  const pickSuggestion = (index, key) => {
    const match = fieldSuggestions.find((s) => s.key === key);
    updateRow(index, { key, ...(match && !rows[index].label ? { label: match.label } : {}) });
  };

  const apply = () => {
    onApply({
      paperKey,
      customSize,
      paperSize: { width: paper.width, height: paper.height, ...(paper.name ? { name: paper.name } : {}) },
      fields: generated,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutList className="w-5 h-5 text-blue-600" />
            Quick layout
          </DialogTitle>
          <DialogDescription>
            List the fields in order with paper size and spacing; the badge is laid out for you. Fine-tune by dragging afterwards.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6">
            <div className="space-y-5 min-w-0">
              <section className="rounded-xl border border-gray-200 p-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">Paper & spacing</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="col-span-2 sm:col-span-1">
                    <Label className="text-xs text-gray-600">Paper size</Label>
                    <select className={selectClass} value={paperKey} onChange={(e) => setPaperKey(e.target.value)}>
                      {Object.entries(paperSizes).map(([key, size]) => (
                        <option key={key} value={key}>{size.name}</option>
                      ))}
                    </select>
                  </div>
                  <NumberField label="Start from top (in)" value={spacing.top} onChange={(top) => setSpacing((s) => ({ ...s, top }))} max={paper.height} />
                  <NumberField label="Gap between fields (in)" value={spacing.gap} onChange={(gap) => setSpacing((s) => ({ ...s, gap }))} step={0.02} max={2} />
                  <NumberField label="Side margin (in)" value={spacing.sidePadding} onChange={(sidePadding) => setSpacing((s) => ({ ...s, sidePadding }))} max={paper.width / 2} />
                  {paperKey === 'custom' && (
                    <>
                      <NumberField label="Width (in)" value={customSize.width} step={0.1} min={1}
                        onChange={(width) => setCustomSize((c) => ({ ...c, width: width || 1 }))} />
                      <NumberField label="Height (in)" value={customSize.height} step={0.1} min={1}
                        onChange={(height) => setCustomSize((c) => ({ ...c, height: height || 1 }))} />
                    </>
                  )}
                </div>
              </section>

              <section className="rounded-xl border border-gray-200 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h3 className="text-sm font-semibold text-gray-800">Fields, top to bottom</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(TYPE_META).map(([type, meta]) => {
                      const Icon = meta.icon;
                      return (
                        <Button key={type} type="button" variant="outline" size="sm" onClick={() => setRows((prev) => [...prev, newRow(type)])}>
                          <Icon className="w-4 h-4 mr-1" /> {meta.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>

                <datalist id="quick-layout-keys">
                  {fieldSuggestions.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </datalist>

                {rows.length === 0 && <p className="text-sm text-gray-500 py-6 text-center">Add a text field or QR code to start.</p>}

                <div className="space-y-3">
                  {rows.map((row, index) => {
                    const Icon = TYPE_META[row.type]?.icon || Type;
                    return (
                      <div key={index} className="rounded-lg border border-gray-200 bg-gray-50/60 p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-700">{index + 1}</span>
                          <Icon className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">{TYPE_META[row.type]?.label}</span>
                          <div className="ml-auto flex gap-1">
                            <Button type="button" variant="ghost" size="sm" onClick={() => moveRow(index, -1)} disabled={index === 0} aria-label="Move up">
                              <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => moveRow(index, 1)} disabled={index === rows.length - 1} aria-label="Move down">
                              <ArrowDown className="w-4 h-4" />
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeRow(index)} aria-label="Remove field">
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                          <div className="col-span-2">
                            <Label className="text-xs text-gray-600">Field key *</Label>
                            <Input
                              list="quick-layout-keys"
                              value={row.key}
                              onChange={(e) => pickSuggestion(index, e.target.value)}
                              placeholder={row.type === 'qrCode' ? 'qrCodeBase64' : 'e.g. name, City/ Country'}
                              className="h-9"
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs text-gray-600">Label (preview text)</Label>
                            <Input value={row.label} onChange={(e) => updateRow(index, { label: e.target.value })} className="h-9" />
                          </div>
                          {row.type === 'text' ? (
                            <>
                              <NumberField label="Size (pt)" value={row.fontSize} step={1} min={6} max={96}
                                onChange={(fontSize) => updateRow(index, { fontSize: fontSize || 14 })} />
                              <NumberField label="Lines" value={row.lines} step={1} min={1} max={4}
                                onChange={(lines) => updateRow(index, { lines: Math.max(1, Math.round(lines || 1)) })} />
                              <div className="col-span-2">
                                <Label className="text-xs text-gray-600">Text case</Label>
                                <select className={selectClass} value={row.textCase} onChange={(e) => updateRow(index, { textCase: e.target.value })}>
                                  {TEXT_CASES.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                </select>
                              </div>
                              <label className="col-span-2 flex items-end gap-2 pb-2 text-sm text-gray-700">
                                <input type="checkbox" className="h-4 w-4" checked={row.bold} onChange={(e) => updateRow(index, { bold: e.target.checked })} />
                                Bold
                              </label>
                            </>
                          ) : (
                            <NumberField label="Size (in)" value={row.size} step={0.05} min={0.3} max={paper.width}
                              onChange={(size) => updateRow(index, { size: size || 1 })} />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </div>

            <aside className="lg:sticky lg:top-0 self-start space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800">Preview</h3>
                {backgroundImage && (
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" checked={showBackground} onChange={(e) => setShowBackground(e.target.checked)} />
                    Background
                  </label>
                )}
              </div>
              <div className="flex justify-center rounded-xl bg-gray-100 p-4">
                <DynamicBadgeRenderer
                  badgeConfig={{
                    paperSize: paper,
                    fields: generated,
                    backgroundImage: showBackground ? backgroundImage : null,
                  }}
                  userData={previewData}
                  showPreview
                  className="max-w-full"
                />
              </div>
              <p className={`text-xs ${overflows ? 'text-red-600' : 'text-gray-500'}`}>
                {overflows
                  ? `Fields end at ${bottom.toFixed(2)} in, past the ${paper.height} in paper height. Reduce the start, gap or sizes.`
                  : `${generated.length} field${generated.length === 1 ? '' : 's'} · ends at ${bottom.toFixed(2)} of ${paper.height} in`}
              </p>
            </aside>
          </div>
        </DialogBody>

        <div className="flex flex-col-reverse gap-2 border-t border-gray-200 p-6 sm:flex-row sm:items-center">
          <p className="text-xs text-gray-500 sm:mr-auto">Applying replaces the current fields. Save the badge afterwards to store it.</p>
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={apply} disabled={!generated.length}>Apply layout</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BadgeQuickLayoutDialog;
